import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { getRouteApi } from '@tanstack/react-router';

import { useCustomerSubmissions } from '@/api/generated';
import PageLayout from '@/components/blocks/layout/page-layout';
import {
  NavBreadcrumb,
  NavBreadcrumbItem,
} from '@/components/blocks/shared/nav-breadcrumb';
import {
  ALL_DEPARTMENTS,
  SubmissionsDepartmentFilter,
} from '@/components/blocks/submissions/submissions-department-filter';
import { SubmissionsTable } from '@/components/blocks/submissions/submissions-table';
import { SubmissionsTableSkeleton } from '@/components/blocks/submissions/submissions-table-skeleton';
import { CommonError } from '@/components/ui/common-error';

const route = getRouteApi('/app/shelves/$customerId');

const Submissions = () => {
  const { t } = useTranslation();
  const { customerId } = route.useParams();
  const { data, isLoading, isError } = useCustomerSubmissions(
    Number(customerId)
  );
  const [departmentCode, setDepartmentCode] = useState(ALL_DEPARTMENTS);

  // The endpoint returns every submission unpaginated, so both the dropdown
  // options and the filtering itself stay client-side.
  const departments = useMemo(
    () =>
      [
        ...new Set(
          (data?.items ?? [])
            .map((item) => item.departmentCode)
            .filter((code): code is string => Boolean(code))
        ),
      ].sort(),
    [data]
  );
  const filteredSubmissions = useMemo(() => {
    const items = data?.items ?? [];
    return departmentCode === ALL_DEPARTMENTS
      ? items
      : items.filter((item) => item.departmentCode === departmentCode);
  }, [data, departmentCode]);

  const navigationCrumbs: Array<NavBreadcrumbItem> = [
    {
      text: t('customers.title'),
      path: '/app/shelves',
    },
    {
      text: `${customerId}`,
      path: '/app/shelves/$customerId',
      params: { customerId },
    },
  ];

  if (isLoading) {
    return <SubmissionsTableSkeleton />;
  }

  if (isError || !data) {
    return <CommonError />;
  }

  return (
    <PageLayout>
      <div className='page-top-container'>
        <div className='flex items-center gap-4'>
          <span className='page-top-title'>{t('submissions.title')}</span>
          <span className='page-top-title font-normal'>
            <Trans
              i18nKey='submissions.filter.results'
              // count drives the _one/_other plural forms, so it carries the
              // total; the noun "submission(s)" refers to the total, not the
              // filtered subset.
              values={{
                shown: filteredSubmissions.length,
                count: data.items.length,
              }}
              components={{
                bold: <span className='text-primary font-semibold' />,
              }}
            />
          </span>
        </div>
        <div className='flex items-center gap-4'>
          <SubmissionsDepartmentFilter
            departments={departments}
            value={departmentCode}
            onValueChange={setDepartmentCode}
          />
          <NavBreadcrumb crumbs={navigationCrumbs} />
        </div>
      </div>

      <SubmissionsTable
        submissions={filteredSubmissions}
        customerId={customerId}
      />
    </PageLayout>
  );
};

export default Submissions;
